<template>
  <v-container>
    <v-row justify="center">
      <v-col class="pa-0" cols="12" md="6">
        <v-stepper
          v-if="status === 'pending-payment'"
          v-model="step"
          vertical>
          <v-card
            tile
            flat
            color="primary">
            <v-card-text>
              <v-row>
                <v-col>
                  <div class="title white--text">
                    {{ restaurant.name }}
                  </div>
                  <div class="subtitle white--text">
                    Orden: {{ order }}
                  </div>
                </v-col>
                <v-col>
                  <div class="subtitle white--text">
                    Subtotal:
                    {{ total | toFixNumber(2) | prefix('$') }}
                  </div>
                  <div class="subtitle white--text">
                    Propina:
                    {{ calculateAmount(tip) | toFixNumber(2) | prefix('$') }}
                  </div>
                </v-col>
                <v-col>
                  <div class="subtitle white--text">
                    Total a pagar:
                  </div>
                  <div class="title white--text font-weight-black">
                    {{ calculateTotal(total) | toFixNumber(2) | prefix('$') }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-stepper-step
            :complete="step > 1"
            :editable="step > 1"
            step="1">
            Datos del cliente
          </v-stepper-step>

          <v-stepper-content step="1">
            <v-form ref="customerForm">
              <v-row>
                <v-col cols="12" md="12">
                  <v-text-field
                    v-model="customer.fullName"
                    ref="fullName"
                    label="Nombre completo"
                    placeholder="Juan Alberto Pérez Hernández"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                </v-col>
                <v-col class="d-flex" cols="12" md="12">
                  <div class="mr-3">
                    <v-autocomplete
                      v-model="phoneCode"
                      ref="phoneCode"
                      label="Código"
                      placeholder="+52"
                      :items="countrycodes"
                      :filter="searchCode"
                      item-text="dial_code"
                      item-value="dial_code"
                      dense
                      rounded
                      hide-details
                      outlined
                      :rules="rules.required">
                      <template v-slot:item="{ item }">
                        {{ item.dial_code }} | {{ item.name }}
                      </template>
                    </v-autocomplete>
                  </div>
                  <div>
                    <v-text-field
                      v-model="customer.phone"
                      ref="phone"
                      label="Teléfono"
                      placeholder="5512345678"
                      dense
                      rounded
                      hide-details
                      outlined
                      :rules="rules.required"/>
                  </div>
                </v-col>
                <v-col cols="12" md="12">
                  <v-text-field
                    v-model="customer.email"
                    ref="email"
                    label="Email"
                    placeholder="alguien@algo.com"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="[...rules.required, ...rules.email]"/>
                </v-col>
              </v-row>
            </v-form>
            <div class="d-flex mt-4">
              <v-spacer/>
              <v-btn
                color="primary"
                small
                rounded
                depressed
                @click="validateAnContinue">
                Continuar
              </v-btn>
            </div>
          </v-stepper-content>

          <v-stepper-step
            :complete="step > 2"
            :editable="step > 2"
            step="2">
            Dirección de envío
          </v-stepper-step>

          <v-stepper-content step="2">
            <v-form ref="addressForm">
              <v-row>
                <v-col cols="12" md="12">
                  <v-text-field
                    v-model="address.street"
                    label="Calle"
                    placeholder="Agustín Melgar"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="address.exterior"
                    label="Número exterior"
                    placeholder="12"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="address.interior"
                    label="Número interior"
                    placeholder="3 (opcional)"
                    dense
                    rounded
                    hide-details
                    outlined/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="address.neighborhood"
                    label="Colonia"
                    placeholder="Condesa"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="address.city"
                    label="Ciudad"
                    placeholder="Jalapa"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="address.state"
                    label="Estado"
                    placeholder="Veracruz"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="address.zipCode"
                    label="Código postal"
                    placeholder="06700"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                </v-col>
              </v-row>
            </v-form>
            <div class="d-flex mt-4">
              <v-spacer/>
              <v-btn
                color="primary"
                small
                rounded
                depressed
                @click="validateAnContinue">
                Ir a pagar
              </v-btn>
            </div>
          </v-stepper-content>

          <v-stepper-step
            :complete="step > 3"
            :editable="step > 3"
            step="3">
            Tarjeta
          </v-stepper-step>

          <v-stepper-content step="3">
            <v-form ref="paymentForm">
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="form.cardNumber"
                    label="Número de tarjeta"
                    placeholder="123456789012"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                    <div class="mt-2">
                      <img :src="cardLogo">
                    </div>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.securityCode"
                    label="CVV"
                    id="cvv"
                    placeholder="123"
                    dense
                    rounded
                    hide-details
                    outlined
                    :rules="rules.required"/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.cardExpirationMonth"
                    label="Mes de expiración"
                    placeholder="11"
                    dense
                    rounded
                    outlined
                    hide-details
                    :items="months"
                    :rules="rules.required"/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.cardExpirationYear"
                    label="Año de expiración"
                    placeholder="2030"
                    dense
                    rounded
                    outlined
                    hide-details
                    :items="years"
                    :rules="rules.required"/>
                </v-col>
                <v-col cols="12">
                  <div>
                    Agrega propina:
                  </div>
                  <v-chip-group
                    v-model="tip"
                    active-class="primary white--text"
                    mandatory>
                    <v-chip small :value="0">
                      $0.00
                    </v-chip>
                    <v-chip small :value="5">
                      <span class="mr-2">
                        5%
                      </span>
                      <span>
                        {{ calculateAmount(5) | toFixNumber(2) | prefix('$') }}
                      </span>
                    </v-chip>
                    <v-chip small :value="10">
                      <span class="mr-2">
                        10%
                      </span>
                      <span>
                        {{ calculateAmount(10) | toFixNumber(2) | prefix('$') }}
                      </span>
                    </v-chip>
                    <v-chip small :value="15">
                      <span class="mr-2">
                        15%
                      </span>
                      <span>
                        {{ calculateAmount(15) | toFixNumber(2) | prefix('$') }}
                      </span>
                    </v-chip>
                    <v-chip small :value="20">
                      <span class="mr-2">
                        20%
                      </span>
                      <span>
                        {{ calculateAmount(20) | toFixNumber(2) | prefix('$') }}
                      </span>
                    </v-chip>
                  </v-chip-group>
                  <div class="subtitle-2 grey--text">
                    Tu colaboración va directamente a la bolsa de todo el equipo que está trabajando hoy en tu pedido.
                  </div>
                  <div class="subtitle-2 grey--text font-weight-black">
                    Gracias por apoyar nuestra comunidad.
                  </div>
                </v-col>
                <input v-model="form.email" type="hidden" name="email"/>
                <input v-model="form.cardNumber" type="hidden" data-checkout="cardNumber"/>
                <input v-model="form.securityCode" type="hidden" data-checkout="securityCode"/>
                <input v-model="form.cardExpirationMonth" type="hidden" data-checkout="cardExpirationMonth"/>
                <input v-model="form.cardExpirationYear" type="hidden" data-checkout="cardExpirationYear"/>
                <input type="hidden" data-checkout="cardholderName" value="APRO"/>
                <input v-model="form.amount" type="hidden" name="amount" id="amount"/>
                <input v-model="form.paymentMethodId" type="hidden" name="paymentMethodId" />
              </v-row>
            </v-form>
            <div class="d-flex mt-4">
              <v-spacer/>
              <v-btn
                color="primary"
                small
                rounded
                depressed
                @click="doPay">
                Pagar
              </v-btn>
            </div>
          </v-stepper-content>
        </v-stepper>
        <v-card
          v-if="status === 'rejected'"
          tile
          flat
          color="red">
          <v-card-text>
            <div class="title white--text">
              <v-icon class="mr-3">
                mdi-check-bold
              </v-icon>
              ¡Tu tarjeta ha sido rechazada!
            </div>
            <div class="subtitle white--text">
              {{ rejectionReason }}
            </div>
          </v-card-text>
        </v-card>
        <v-card
          v-if="status === 'in-review'"
          tile
          flat
          color="orange">
          <v-card-text>
            <div class="title white--text">
              <v-icon class="mr-3">
                mdi-check-bold
              </v-icon>
              En revisión
            </div>
            <div class="subtitle white--text">
              Esta orden se encuentra en revisión, te notificaremos pronto si ha sido aprobada o rechazada y la razón por la cual la tuvimos que rechazarla.
            </div>
          </v-card-text>
        </v-card>
        <v-card
          v-if="status === 'canceled'"
          tile
          flat
          color="red">
          <v-card-text>
            <div class="title white--text">
              <v-icon class="mr-3">
                mdi-check-bold
              </v-icon>
              ¡Rechazado!
            </div>
            <div class="subtitle white--text">
              Esta orden fue cancelada, sentimos las molestias, si deseas más información por favor contáctanos.
            </div>
          </v-card-text>
        </v-card>
        <v-card
          v-if="status === 'approved'"
          tile
          flat
          color="primary">
          <v-card-text>
            <div class="title white--text">
              <v-icon class="mr-3">
                mdi-check-bold
              </v-icon>
              ¡Su pago se realizó con éxito!
            </div>
            <div class="subtitle white--text">
              Muchas gracias por su compra, visita esta url para verificar actualización de tu pedido.
            </div>
          </v-card-text>
        </v-card>
        <v-card
          v-if="status === 'cooking'"
          tile
          flat
          color="info">
          <v-card-text>
            <div class="title white--text">
              <v-icon class="mr-3">
                mdi-check-bold
              </v-icon>
              En preparación
            </div>
            <div class="subtitle white--text">
              Ya estamos preparando tu comida, refresca esta url para ver actualizaciones de tu orden.
            </div>
          </v-card-text>
        </v-card>
        <v-card
          v-if="status === 'delivering'"
          tile
          flat
          color="info">
          <v-card-text>
            <div class="title white--text">
              <v-icon class="mr-3">
                mdi-check-bold
              </v-icon>
              En camino
            </div>
            <div class="subtitle white--text">
              Hemos entregado tu orden a un repartidor y ya se encuentra en camino.
            </div>
          </v-card-text>
        </v-card>
        <v-card
          v-if="status === 'delivered'"
          tile
          flat
          color="primary">
          <v-card-text>
            <div class="title white--text">
              <v-icon class="mr-3">
                mdi-check-bold
              </v-icon>
              ¡Orden entregada!
            </div>
            <div class="subtitle white--text">
              Tu orden ha sido entregada, muchas gracias por tu compra, te esperamos pronto.
            </div>
          </v-card-text>
        </v-card>
        <div class="subtitle-2 grey--text my-4">
          Sitio seguro para compras por internet.
        </div>
        <div>
          <img class="secure-logo mr-3" src="@/assets/cnbv.png" alt="">
          <img class="secure-logo" src="@/assets/pci.svg" alt="">
        </div>
      </v-col>
    </v-row>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import countrycodes from '@/lib/countrycodes'

export default {
  data () {
    return {
      countrycodes,
      overlay: false,
      doSubmit: false,
      step: 1,
      customer: {
        fullName: null,
        phone: null,
        email: null
      },
      address: {
        street: null,
        interior: '',
        exterior: null,
        neighborhood: null,
        city: null,
        state: null,
        zipCode: null
      },
      payment: {
        token: null,
        paymentId: null
      },
      form: {
        email: null,
        cardNumber: null,
        paymentMethodId: null,
        securityCode: null,
        amount: null,
        cardExpirationMonth: null,
        cardExpirationYear: null
      },
      tip: 0,
      phoneCode: null,
      months: [ '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12' ],
      restaurant: {
        name: null,
        picture: null,
        address: null
      },
      total: 0,
      status: 'pending-payment',
      statusDetailed: '',
      rules: {
        required: [v => !!v || this.$t('validations.required')],
        email: [
          v => !!v || this.$t('validations.email.required'),
          v => /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v) || this.$t('validations.email.valid')
        ]
      },
      cardLogo: null
    }
  },
  computed: {
    rejectionReason () {
      return {
        cc_rejected_bad_filled_card_number: 'Revisa el número de tarjeta.',
        cc_rejected_bad_filled_date: 'Revisa la fecha de vencimiento.',
        cc_rejected_bad_filled_other: 'Revisa los datos.',
        cc_rejected_bad_filled_security_code: 'Revisa el código de seguridad.',
        cc_rejected_blacklist: 'No pudimos procesar tu pago.',
        cc_rejected_call_for_authorize: 'Debes autorizar ante payment_method_id el pago de amount a Mercado Pago',
        cc_rejected_card_disabled: 'Llama a payment_method_id para que active tu tarjeta. El teléfono está al dorso de tu tarjeta.',
        cc_rejected_card_error: 'No pudimos procesar tu pago.',
        cc_rejected_duplicated_payment: 'Ya hiciste un pago por ese valor. Si necesitas volver a pagar usa otra tarjeta u otro medio de pago.',
        cc_rejected_high_risk: 'Tu pago fue rechazado. Elige otro de los medios de pago, te recomendamos con medios en efectivo.',
        cc_rejected_insufficient_amount: 'Tu payment_method_id no tiene fondos suficientes.',
        cc_rejected_invalid_installments: 'payment_method_id no procesa pagos en installments cuotas.',
        cc_rejected_max_attempts: 'Llegaste al límite de intentos permitidos. Elige otra tarjeta u otro medio de pago.',
        cc_rejected_other_reason: 'payment_method_id no procesó el pago'
      }[this.statusDetailed]
    },
    years () {
      const date = new Date()
      const thisYear = date.getFullYear()
      const y = []
      for (let index = 0; index < 15; index++) {
        y.push(thisYear + index)
      }
      return y
    },
    order () {
      return this.$route.params.id
    },
    slug () {
      return this.$route.params.restaurantSlug
    }
  },
  watch: {
    'form.cardNumber': function (val) {
      this.guessingPaymentMethod(val)
    }
  },
  async mounted () {
    this.overlay = true
    try {
      await window.Mercadopago.setPublishableKey(process.env.VUE_APP_MP_PUBLIC_KEY)
      const response = await this.$api.get(`/v1/pay/${this.slug}/orders/${this.order}`)
      this.restaurant = response.restaurant
      this.total = response.sale.total
      this.status = response.sale.status
      this.statusDetailed = response.sale.statusDetailed
    } catch (error) {
      this.$toasted.show(error.message, { duration: 3000 })
    }
    this.overlay = false
  },
  methods: {
    searchCode (item, queryText, itemText) {
      const name = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return name.indexOf(searchText) >= 0
    },
    getBin: function (val) {
      const bin = val.substring(0, 6)
      return bin
    },
    guessingPaymentMethod: async function (val) {
      let bin = this.getBin(val)
      if (bin.length >= 6) {
        window.Mercadopago.getPaymentMethod({
          'bin': bin
        }, this.setPaymentMethodInfo)
      }
    },
    setPaymentMethodInfo: function (status, response) {
      if (status === 200) {
        this.form.paymentMethodId = response[0].id
        this.cardLogo = response[0].secure_thumbnail
      } else {
        this.$toasted.show(response, { duration: 3000 })
      }
    },
    sdkResponseHandler: async function (status, response) {
      if (status !== 200 && status !== 201) {
        this.resetForm()
        this.$toasted.show('Verifica los datos de tu tarjeta', { duration: 3000 })
      } else {
        this.overlay = true
        this.payment.token = response.id
        this.payment.paymentId = this.form.paymentMethodId
        this.customer.phone = `${this.phoneCode}${this.customer.phone}`
        this.doSubmit = true
        await this.processPayment()
        this.overlay = false
      }
    },
    doPay: async function () {
      if (!this.doSubmit) {
        var $form = this.$refs.paymentForm.$el

        await window.Mercadopago.createToken($form, this.sdkResponseHandler)

        return false
      } else {
        await this.processPayment()
      }
    },
    processPayment: async function () {
      const payload = {
        customer: this.customer,
        payment: this.payment,
        address: this.address,
        tip: this.calculateAmount(this.tip)
      }
      try {
        const response = await this.$api.post(`/v1/pay/${this.slug}/orders/${this.order}`, payload)
        this.status = response.sale.status
        this.statusDetailed = response.sale.statusDetailed
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    resetForm () {
      if (this.$refs.paymentForm) this.$refs.paymentForm.reset()

      this.form = {
        email: null,
        cardNumber: null,
        paymentMethodId: null,
        securityCode: null,
        amount: null,
        cardExpirationMonth: null,
        cardExpirationYear: null
      }

      this.payment = {
        token: null,
        paymentId: null
      }
    },
    validateAnContinue () {
      if (Number(this.step) === 1) {
        const isValid = this.$refs.customerForm.validate()
        if (isValid) {
          this.step = 2
        }
        return
      }

      if (Number(this.step) === 2) {
        const isValid = this.$refs.addressForm.validate()
        if (isValid) {
          this.step = 3
        }
      }
    },
    calculateAmount (val) {
      return (val / 100) * this.total
    },
    calculateTotal (val) {
      return this.calculateAmount(this.tip) + val
    }
  }
}
</script>

<style lang="scss">
.secure-logo {
  height: 60px;
  vertical-align: middle;
}
</style>
